import React from "react";
import { RouteComponentProps } from "react-router";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Row, Col, Button, Modal, message, Table, Spin, Empty } from "antd";
import Card from "../components/Card";
import {
  User,
  EntityException,
  Entity,
  Company,
  Customer,
  Entities,
  FormFieldConfig,
  PluralEntities,
  APIError,
} from "../helpers/shared-interfaces";
import {
  request,
  generateNameFromCustomer,
  handleValidationErrors,
  mapAPIErrorToFormError,
  generateDisplayErrorMessage,
  generateExclusionTypes,
  responseHandler,
} from "../helpers/utilities";
import { FormInstance } from "antd/lib/form";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import {
  CREATE_ENTITLEMENT_EXCEPTION,
  GET_USERS,
  GET_COMPANIES,
  GET_CUSTOMERS,
  GET_EXCEPTIONS,
  GET_ENTITLEMENT,
  DELETE_ENTITY_EXCEPTION,
  MASS_GET_CUSTOMERS,
  MASS_GET_COMPANY,
} from "../gql/entitlements";
import { AxiosResponse } from "axios";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import * as Sentry from "@sentry/browser";

interface State {
  name: string;
  isLoading: boolean;
  hasChanged: boolean;
  entitlementId: string | number;
  showDialog: boolean;
  users: User[];
  exceptions: EntityException[];
  entitlementName: string;
  exceptionId: string | number;
  idToBeDeleted: string;
  entities: Entity[];
  entityMap: MapIDToEntity;
  showNewExceptionModal: boolean;
  isCreatingException: boolean;
  allUsers: User[];
  allEntities: Entity[];
}

interface MapIDToEntity {
  [x: string]: Entity;
}

interface RouteParams {
  id: string;
}

export interface TransformedData {
  ids: string[];
  entity: string;
}


export interface ColumnProps {
  title: string;
  dataIndex: string;
  key: string;
  width: string;
  render: (text: string, record: EntityException) => JSX.Element;
}

export const ColumnsCreatorHOC = function (
  toggleDeleteConfirmationModal: (id: string) => void,
  entityMap: MapIDToEntity,
): ColumnProps[] {
  return [
    {
      title: "User",
      dataIndex: "user",
      key: "User",
      width: "30%",
      render(text: string, record: EntityException): JSX.Element {
        return <span>{record.user.name}</span>;
      },
    },
    {
      title: "Exception Type",
      dataIndex: "type",
      key: "Exception Type",
      width: "10%",
      render(text: string, record: EntityException): JSX.Element {
        return <span>{record.exceptionType}</span>;
      },
    },
    {
      title: "Entity Name",
      dataIndex: "name",
      key: "Entity Name",
      width: "30%",
      render(text: string, record: EntityException): JSX.Element {
        if (entityMap[record.entityId]) {
          return <span>{entityMap[record.entityId].name}</span>;
        }
        return <span>{record.entityId}</span>;
      },
    },
    {
      title: "Actions",
      dataIndex: "action",
      key: "actions",
      width: "30%",
      render(text: string, record: EntityException): JSX.Element {
        return (
          <Col
            style={{
              textAlign: "right",
            }}
            span={12}
          >
            <EntitledSection entitlement={entitlements.Departments.delete}>
              <Button
                icon={<DeleteOutlined />}
                size="large"
                id="test-delete-exception-button"
                onClick={(): void => {
                  toggleDeleteConfirmationModal(record.id);
                }}
              >
                Delete
              </Button>
            </EntitledSection>
          </Col>
        );
      },
    },
  ];
};

export function injectKeyProp(data: EntityException[]): EntityException[] {
  const tempData: EntityException[] = data.map(
    (exception: EntityException): EntityException => {
      return { ...exception, key: exception.id };
    },
  );
  return tempData;
}

function ModalWrapper(
  showDialog: boolean,
  deleteException: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteException}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this exception?</div>
    </Modal>
  );
}

export const NewExceptionUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  isCreatingException: boolean;
  users: User[];
  entities: Entity[];
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      name: "userId",
      rule: "string",
      inputType: "select",
      required: true,
      label: "User",
      placeholder: "Select User",
      selectOptions: props.users,
      colConfig: { span: 24 },
    },
    {
      name: "exceptionType",
      rule: "string",
      inputType: "select",
      required: true,
      label: "Exclusion Type",
      placeholder: "Exclusion Type",
      selectOptions: generateExclusionTypes(),
      colConfig: { span: 24 },
    },
    {
      name: "entityId",
      rule: "string",
      inputType: "select",
      required: true,
      label: "Entity",
      placeholder: "Entity",
      selectOptions: props.entities,
      colConfig: { span: 24 },
    },
  ];

  return (
    <Spin spinning={props.isCreatingException}>
      <BaseForm
        layout="vertical"
        reference={props.reference}
        fieldsToHide={[]}
        fields={fields}
        gutter={[16, 16]}
      />
    </Spin>
  );
};

export default class ViewEntitlement extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: React.RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      entityMap: {},
      users: [],
      entities: [],
      isLoading: true,
      exceptionId: "",
      entitlementId: "",
      name: "",
      hasChanged: false,
      showDialog: false,
      entitlementName: "",
      exceptions: [],
      idToBeDeleted: "",
      showNewExceptionModal: false,
      isCreatingException: false,
      allUsers: [],
      allEntities: [],
    };
    this.reference = React.createRef<FormInstance>();
    this.deleteEntityException = this.deleteEntityException.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.getEntitlementData = this.getEntitlementData.bind(this);
    this.toggleCreateExceptionModal = this.toggleCreateExceptionModal.bind(this);
    this.createException = this.createException.bind(this);
  }

  toggleDeleteConfirmationModal(id?: string): void {
    const prevState = this.state.showDialog;
    const finalId = id || "";
    this.setState({ showDialog: !prevState, idToBeDeleted: finalId });
  }

  massGetEntities = async (exceptions: EntityException[]): Promise<void> => {
    const data = this.transformData(exceptions);
    let entities: Entity[] = [];

    interface Response {
      data: {
        bulkCompanies?: Company[];
        bulkCustomers?: Customer[];
      };
    }

    this.setState({ isLoading: true });

    switch (data.entity.toLowerCase()) {
      case Entities.user:
        request({
          query: MASS_GET_CUSTOMERS,
          variables: { ids: data.ids },
        }).then(({ data }: AxiosResponse<Response>): void => {
          const customers = data.data.bulkCustomers as Customer[];
          entities = customers.map((c: Customer): Entity => {
            return {
              id: c.id,
              name: generateNameFromCustomer(c),
              petraId: c.petraId,
            };
          });
          this.setState({ entities }, (): void => {
            this.constructEntityMap();
          });
        });
        break;
      case Entities.customer:
        request({
          query: MASS_GET_CUSTOMERS,
          variables: { ids: data.ids },
        }).then(({ data }: AxiosResponse<Response>): void => {
          const customers = data.data.bulkCustomers as Customer[];
          entities = customers.map((c: Customer): Entity => {
            return {
              id: c.id,
              name: generateNameFromCustomer(c),
              petraId: c.petraId,
            };
          });
          this.setState({ entities }, (): void => {
            this.constructEntityMap();
          });
        });
        break;
      case Entities.company:
        try {
          const response: AxiosResponse<Response> = await request({
            query: MASS_GET_COMPANY,
            variables: { ids: data.ids },
          });

          const companies = response.data.data.bulkCompanies as Company[];
          entities = companies.map((c: Company): Entity => {
            return {
              id: c.id,
              name: c.name,
              petraId: c.petraId,
            };
          });
          this.setState({ entities }, (): void => {
            this.constructEntityMap();
          });
        } catch (error) {
          message.error(`failed to load data - ${generateDisplayErrorMessage(error)}`);
        }

        break;
    }

    this.setState({ isLoading: false });
  };

  getExceptions = async (): Promise<void> => {
    interface Response {
      data: {
        entityExceptions: EntityException[];
      };
    }

    const { entitlementId } = this.state;
    this.setState({ isLoading: true });

    request({
      query: GET_EXCEPTIONS,
      variables: { entitlementId },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({ exceptions: data.data.entityExceptions });
        this.massGetEntities(data.data.entityExceptions);
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  };

  constructEntityMap = (): void => {
    const map: MapIDToEntity = {};
    this.state.entities.forEach((e: Entity): void => {
      map[e.id] = e;
    });

    this.setState({ entityMap: map });
  };

  getEntitlementData = async (): Promise<void> => {
    const { entitlementId: id } = this.state;
    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<any> = await request({
        query: GET_ENTITLEMENT,
        variables: { id },
      });

      if (!response.data.errors) {
        this.setState({
          name: response.data.data.entitlement.name,
        });
        this.constructEntityMap();
      } else {
        message.error("Failed to load entitlement");
      }
    } catch (error) {}

    this.setState({ isLoading: false });
  };

  async deleteEntityException(): Promise<void> {
    const { idToBeDeleted } = this.state;

    interface Response {
      data: {
        deleteEntityException: {
          result: boolean;
        };
      };
    }

    this.setState({ isLoading: true });

    try {
      const { data }: AxiosResponse<Response> = await request({
        query: DELETE_ENTITY_EXCEPTION,
        variables: { id: idToBeDeleted },
      });

      this.setState({ isLoading: false, showDialog: false });

      if (data.data.deleteEntityException.result) {
        message.success("Exception deleted successfuly");
        this.getExceptions();
      } else {
        message.error("Failed to delete exception");
      }
    } catch (error) {
      Sentry.captureException(error);
      message.error(`Request Failed. - ${generateDisplayErrorMessage(error)}`);
    }
  }

  toggleCreateExceptionModal(): void {
    const opening = !this.state.showNewExceptionModal;
    this.setState({ showNewExceptionModal: opening });
    if (opening && this.state.allUsers.length === 0) {
      this.getAllUsersForDropdown();
      this.getAllEntitiesForDropdown();
    }
  }

  getAllUsersForDropdown = async (): Promise<void> => {
    try {
      const response = await request({ query: GET_USERS });
      this.setState({ allUsers: response.data.data.users });
    } catch(e) {}
  };

  getAllEntitiesForDropdown = (): void => {
    let entities: Entity[] = [];

    interface Response {
      data: {
        companies?: Company[];
        customers?: Customer[];
        users?: User[];
      };
    }

    const entity = this.state.name.split("::")[0];
    this.setState({ isCreatingException: true });

    switch (entity.toLowerCase()) {
      case PluralEntities.companies:
        request({ query: GET_COMPANIES }).then(
          ({ data }: AxiosResponse<Response>): void => {
            const companies = data.data.companies || [];
            entities = companies.map((c: Company): Entity => ({
              id: c.id,
              name: c.name,
              petraId: c.petraId,
            }));
            this.setState({ allEntities: entities, isCreatingException: false });
          },
        );
        break;

      case PluralEntities.customers:
        request({ query: GET_CUSTOMERS }).then(
          ({ data }: AxiosResponse<Response>): void => {
            const customers = data.data.customers || [];
            entities = customers.map((c: Customer): Entity => ({
              id: c.id,
              name: generateNameFromCustomer(c),
              petraId: c.petraId,
            }));
            this.setState({ allEntities: entities, isCreatingException: false });
          },
        );
        break;

      case PluralEntities.users:
        request({ query: GET_USERS }).then(
          ({ data }: AxiosResponse<Response>): void => {
            const users = data.data.users || [];
            entities = users.map((u: User): Entity => ({
               id: u.id,
               name: u.name,
               petraId: "petraId",
            }));
            this.setState({ allEntities: entities, isCreatingException: false });
          },
        );
        break;
      default:
        this.setState({ isCreatingException: false });
    }
  };

  createException = async (): Promise<void> => {
    const { entitlementId } = this.state;

    interface Response {
      data: {
        createEntityException: {
          entityException: EntityException;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields([
        "userId",
        "entityId",
        "exceptionType",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isCreatingException: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_ENTITLEMENT_EXCEPTION,
        variables: { entitlementId, ...data },
      });

      const { err } = responseHandler<Response>(
        response,
        "createEntityException",
      );

      this.setState({ isCreatingException: false });

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Exception created sucessfully");
        this.toggleCreateExceptionModal();
        this.getExceptions();
      }
    } catch (error) {
      this.setState({ isCreatingException: false });
      message.error(`Failed to create Exception - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  transformData = (entityExceptions: EntityException[]): TransformedData => {
    const transformedData: TransformedData = {
      ids: [],
      entity: "",
    };

    entityExceptions.forEach((e: EntityException): void => {
      transformedData.entity = e.entitlement.relatedModel as string;
      transformedData.ids.push(e.entityId);
    });

    return transformedData;
  };

  componentDidMount(): void {
    this.setState({ entitlementId: this.props.match.params.id }, (): void => {
      this.getEntitlementData();
      this.getExceptions();
    });
  }

  render(): JSX.Element | null {
    const {
      isLoading,
      name,
      showDialog,
      exceptions,
      entityMap,
      showNewExceptionModal,
      isCreatingException,
      allUsers,
      allEntities,
    } = this.state;

    return (
      <Spin spinning={isLoading}>
        <Card>
          <div>
            {ModalWrapper(
              showDialog,
              this.deleteEntityException,
              this.toggleDeleteConfirmationModal,
            )}
            {showNewExceptionModal &&
              ModalCreator({
                title: "Create Exception",
                okType: "primary",
                okText: "Create",
                showModal: showNewExceptionModal,
                onOkay: this.createException,
                toggleModal: this.toggleCreateExceptionModal,
                inscription: NewExceptionUI({
                  reference: this.reference,
                  isCreatingException,
                  users: allUsers,
                  entities: allEntities,
                }),
              })}
            <Row
              style={{
                marginBottom: "20px",
              }}
            >
              <Col
                style={{
                  textAlign: "left",
                }}
                span={12}
              >
                <div>
                  <h1
                    style={{
                      fontWeight: "bold",
                      fontSize: "25px",
                      marginBottom: "0px",
                    }}
                    data-cy="test-entitlement-name"
                  >
                    {name.toUpperCase()}
                  </h1>
                </div>
              </Col>

              <Col
                style={{
                  textAlign: "right",
                }}
                span={12}
              >
                <EntitledSection
                  entitlement={entitlements.Entitlements.exception.create}
                >
                  <Button
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={this.toggleCreateExceptionModal}
                    style={{
                      backgroundColor: "rgb(29, 55, 100)",
                      color: "white",
                      marginRight: "10px",
                    }}
                    data-cy="test-create-exception-button"
                  >
                    Create Exception
                  </Button>
                </EntitledSection>
              </Col>
            </Row>

            <Row>
              {exceptions.length === 0 ? (
                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  <Empty />
                </div>
              ) : (
                <Table
                  style={{ width: "100%" }}
                  bordered={true}
                  columns={ColumnsCreatorHOC(
                    this.toggleDeleteConfirmationModal,
                    entityMap,
                  )}
                  dataSource={injectKeyProp(exceptions)}
                />
              )}
            </Row>
          </div>
        </Card>
      </Spin>
    );
  }
}
